<script setup lang="ts">
import { apps, Commands, Models, useContext, useDialogs, useEnums, useOrderViewContext } from '@hantera/portal-app';
import useApps from '@/composition/use-apps';
import {
  ContextMenuSection,
  ContextMenuSections,
  DataColumnValuesEnumValue,
  DropZone,
  EnumsBar,
  Panel
} from '@hantera/design-system';
import OrderLine from "./OrderLine";
import AppSlotView from "@/AppSlotView.vue";
import ShippingLine from "@/components/Order/Deliveries/ShippingLine";
import ProductSelector from './OrderLine/ProductSelector.vue'
import { v7 } from 'uuid'
import Decimal from "decimal.js";
import { computed } from "vue";

const props = defineProps<{
  delivery: Models.Graph.Delivery
}>();

const orderViewContext = useOrderViewContext();
const portalContext = useContext();
const enums = useEnums()
const dialogs = useDialogs()
const appsCtx = useApps()

async function addTag(value: DataColumnValuesEnumValue) {
  await orderViewContext.pushPendingChange(
    undefined,
    [{
      type: 'addDeliveryTag',
      deliveryId: props.delivery.deliveryId,
      key: value.key,
    }],
    'Added tag {label} to {deliveryNumber}',
    {
      label: value.label,
      tagKey: value.key,
      deliveryNumber: props.delivery.deliveryNumber,
    }
  );
}

async function removeTag(value: DataColumnValuesEnumValue) {
  await orderViewContext.pushPendingChange(
    undefined,
    [{
      type: 'removeDeliveryTag',
      deliveryId: props.delivery.deliveryId,
      key: value.key,
    }],
    'Removed tag {label} from {deliveryNumber}',
    {
      label: value.label,
      tagKey: value.key,
      deliveryNumber: props.delivery.deliveryNumber,
    }
  );
}

async function createOrderLine(product: apps.standardServices.OrderProduct) {
  const orderLineId = v7()

  const commands: Commands.Order.OrderCommand[] = [{
    type: 'createOrderLine',
    orderLineId: orderLineId,
    deliveryId: props.delivery.deliveryId,
    productNumber: product.productNumber,
    description: product.description,
    image: product.image,
    quantity: new Decimal(1),
    unitPrice: product.unitPrice,
    skus: product.skus
  }];

  if (product.taxFactor) {
    commands.push({
      type: 'setOrderLineTax',
      orderLineId: orderLineId,
      taxFactor: product.taxFactor,
    })
  }

  if (product.dynamic) {
    commands.push({
      type: 'setOrderLineDynamicFields',
      orderLineId: orderLineId,
      fields: product.dynamic,
    })
  }

  await orderViewContext.pushPendingChange(
    'addOrderLine_' + orderLineId,
    commands,
    'Add {productNumber} to {deliveryNumber}',
    {
      productNumber: product.productNumber,
      deliveryNumber: props.delivery.deliveryNumber
    }
  )
}

const sortedLines = computed(() => {
  return [...props.delivery.orderLines].sort((a, b) => a.orderLineNumber.localeCompare(b.orderLineNumber));
})

const availableTags = computed(() => {
  return enums.valueSets('delivery', 'tags').value;
})

const contextMenu = computed(() => {

  const deliveryActions: ContextMenuSection = []

  const canReleaseToInvoice =
    orderViewContext.state.orderState === 'confirmed' &&
    props.delivery.deliveryState !== 'cancelled' &&
    props.delivery.deliveryState !== 'cancelledByOrder' &&
    (!props.delivery.releasedToInvoice || props.delivery.orderLines.find(r => !r.releasedToInvoice) !== undefined)

  if (props.delivery.deliveryState === 'open') {
    deliveryActions.push({
      label: 'Release to processing',
      async action() {
        await orderViewContext.pushPendingChange(
          undefined,
          [{
            type: 'releaseDelivery',
            deliveryId: props.delivery.deliveryId,
          }],
          'Released delivery {deliveryNumber} to processing',
          {
            deliveryId: props.delivery.deliveryId,
            deliveryNumber: props.delivery.deliveryNumber,
          }
        )
      }
    })
    deliveryActions.push({
      label: 'Cancel delivery',
      async action() {
        await orderViewContext.pushPendingChange(
          undefined,
          [{
            type: 'cancelDelivery',
            deliveryId: props.delivery.deliveryId,
          }],
          'Cancelled delivery {deliveryNumber}',
          {
            deliveryId: props.delivery.deliveryId,
            deliveryNumber: props.delivery.deliveryNumber,
          }
        )
      }
    })
  }
  if (props.delivery.deliveryState === 'processing') {
    deliveryActions.push({
      label: 'Open delivery',
      async action() {
        if (await dialogs.confirm(
          'Opening a delivery that is in processing can lead to inconsistencies. Make sure there are no external dependencies such as warehouses actively working with the delivery.',
          'Are you sure?',
          undefined,
          undefined,
          true
        )) {
          await orderViewContext.pushPendingChange(
            undefined,
            [{
              type: 'openDelivery',
              deliveryId: props.delivery.deliveryId,
            }],
            'Opened delivery {deliveryNumber}',
            {
              deliveryId: props.delivery.deliveryId,
              deliveryNumber: props.delivery.deliveryNumber,
            }
          )
        }
      }
    })
    deliveryActions.push({
      label: 'Complete delivery',
      async action() {
        await orderViewContext.pushPendingChange(
          undefined,
          [{
            type: 'completeDelivery',
            deliveryId: props.delivery.deliveryId,
          }],
          'Completed delivery {deliveryNumber}',
          {
            deliveryId: props.delivery.deliveryId,
            deliveryNumber: props.delivery.deliveryNumber,
          }
        )
      }
    })
  }

  const result: ContextMenuSections = []

  if (deliveryActions.length > 0) {
    result.push(deliveryActions)
  }

  if (canReleaseToInvoice) {
    const commands: Commands.Order.OrderCommand[] = []
    if (!props.delivery.releasedToInvoice) {
      commands.push({
        type: 'releaseShippingToInvoicing',
        deliveryId: props.delivery.deliveryId,
      })

      for (const ol of props.delivery.orderLines.filter(r => !r.releasedToInvoice)) {
        commands.push({
          type: 'releaseOrderLineToInvoicing',
          orderLineId: ol.orderLineId
        })
      }
    }

    result.push([{
      label: 'Release to invoice',
      async action() {
        await orderViewContext.pushPendingChange(
          undefined,
          commands,
          'Released shipping and orderlines of delivery {deliveryNumber} to invoiceing',
          {
            deliveryId: props.delivery.deliveryId,
            deliveryNumber: props.delivery.deliveryNumber,
          }
        )
      }
    }])
  }

  for (const provider of appsCtx.getServices(apps.standardServices.orderView.contextMenuProvider)) {
    const section = provider.buildDeliveryContextMenu?.(portalContext, orderViewContext, props.delivery)
    if (section && section.length > 0) {
      result.push(section)
    }
  }


  return result
})

</script>

<template>
  <DropZone
    :accepts="`orderline_${orderViewContext.state.orderId}`"
    :metadata="{
      deliveryId: delivery.deliveryId
    }"
  >
    <Panel class="delivery light:box-pop" v-context-menu="contextMenu">
      <div class="delivery-header">
  
        <div
          class="delivery__state"
          :style="`background-color: var(--state-${delivery.deliveryState})`"
          :title="delivery.deliveryState"
        ></div>
        <div class="delivery__header-details">
          <div class="delivery__title">
            <div
              class="delivery__number"
            >{{ delivery.deliveryNumber }}</div>
            <AppSlotView
              :slot="apps.orderViewSlots.delivery.header"
              :context="{
                delivery
              }"
            />
          </div>
          <div class="delivery__tags">
            <EnumsBar
              :selected-values="delivery.tags"
              :available-sets="availableTags"
              @add="addTag"
              @remove="removeTag"
            />
          </div>
        </div>
      </div>
      
      <div class="order-lines">
        <OrderLine
          v-for="line in sortedLines"
          :key="line.orderLineId"
          :order-line="line"
          :delivery="delivery"
        />
        <div
          v-if="delivery.deliveryState === 'open'"
          class="add-order-line box-content p"
        >
          <ProductSelector
            :delivery="delivery"
            @confirm="createOrderLine"
          />
        </div>
        <ShippingLine
          :delivery="delivery"
        />
      </div>
  
      <AppSlotView
        :slot="apps.orderViewSlots.delivery.footer"
        :context="{
          delivery
        }"
      />
    </Panel>
  </DropZone>
</template>

<style scoped>
.delivery {
  max-width: 800px;
}

.delivery-header {
  position: relative;
  padding-left: 10px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.delivery__state {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  border-radius: 5px;
}

.delivery__header-details {
  flex-grow: 1;
}

.delivery__title {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 10px;
  align-items: center;
}

.delivery__number {
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
}

.delivery__inventory,
.delivery__inventory-date {
  white-space: nowrap;
}

.order-lines {
  display: grid;
  grid-template-columns: 45px auto [middle] repeat(99, min-content);
  grid-auto-rows: min-content;
  grid-auto-columns: min-content;
  row-gap: 5px;
}

.add-order-line {
  grid-column: 1 / -1;
}

@container (max-width: 700px) {
  .order-lines {
    grid-template-columns: [middle] auto repeat(99, min-content);
  }
}
</style>